import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listTasksV2 } from 'services/TaskServices';
import { queryKeys } from '../../../config/queryClient';
import { LAYOUT } from '../../../utils/constants';

/**
 * Initial Board Load Hook - SINGLE CACHE ARCHITECTURE
 *
 * Makes ONE API call to fetch page 1 for ALL columns, then populates
 * each column's infinite query cache directly.
 *
 * Benefits:
 * - Single API call on load (fast!)
 * - No dual-cache complexity
 * - All data goes directly into infinite query caches
 * - Mutations only need to update one cache type
 *
 * @param {Array} taskStages - Array of task stage objects with sk property
 * @param {Object} filters - Filter parameters (searchKeyword, practiceArea, assignedTo, sortType, priority, dueDateFilter)
 * @returns {Object} - Query result with loading, error states
 */
export const useInitialBoardLoad = (taskStages, filters = {}, currentView = 'kanban') => {
  const queryClient = useQueryClient();
  const isEnabled = !!taskStages && Array.isArray(taskStages) && taskStages.length > 0 && currentView === 'kanban';

  return useQuery({
    queryKey: ['tasks', 'initial-load', filters, taskStages?.length],

    queryFn: async () => {
      if (!taskStages || taskStages.length === 0) {
        throw new Error('No active task stages found. Please configure task stages first.');
      }

      // Build request payload per Task List v2 API documentation
      const requestData = {
        task_stage_ids: taskStages.map((stage) => stage.task_stage_id),
      };

      // Add filters to body (per API documentation)
      if (filters.practiceArea) {
        requestData.practice_area = filters.practiceArea;
      }
      if (filters.assignedTo && filters.assignedTo.length > 0) {
        // API expects firm_user_ids array instead of assigned_to
        requestData.firm_user_ids = filters.assignedTo;
      }
      if (filters.sortType) {
        requestData.sort_type = filters.sortType;
      }
      if (filters.priority) {
        requestData.priority = filters.priority;
      }
      if (filters.dateFilter) {
        requestData.due_date_filter = filters.dateFilter;
      }

      // Single API call for ALL columns (page 1)
      // Note: keyword is passed as a separate parameter to match API signature
      const response = await listTasksV2({
        limit: LAYOUT.DEFAULT_PAGE_LIMIT,
        data: requestData,
        keyword: filters.searchKeyword || false,
        excludeFromLoading: true,
        case_id: filters.case_id || undefined,
      });

      // Populate EACH column's infinite query cache with page 1 data
      Object.keys(response?.data || {}).forEach((columnId) => {
        const columnData = response.data[columnId];

        // Set initial data in infinite query format (include filters in key)
        queryClient.setQueryData([...queryKeys.tasks.column(columnId), filters], {
          pages: [
            {
              items: columnData?.items || [],
              page: 1,
              hasMore: columnData?.has_more || false,
              totalCount: columnData?.total_count || 0,
            },
          ],
          pageParams: [1],
        });
      });

      return {
        success: true,
        columnsLoaded: Object.keys(response?.data || {}).length,
      };
    },

    enabled: isEnabled,
    retry: 2,
    useErrorBoundary: false,
  });
};
